import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { graderApi, type GraderRunResponse } from '../services/api';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeaderCell, TableRow } from '../components/ui/table';
import {
    appendRunRecord,
    buildResultRows,
    GRADER_ALLOWED_EMAIL,
    isGraderAllowedEmail,
    type GraderResultRow,
    type GraderRunAggregates,
    type GraderRunRecord,
} from '../components/grader/graderEval';

// The route is wrapped in <ProtectedRoute adminOnly>, but the bead requires the
// page + nav to render ONLY for the allowlisted email (same gate as the
// Strategy Simulator and the backend /api/grader surface), so we gate here too
// and redirect everyone else.
export default function GraderPage() {
    const { user } = useAuth();

    if (!isGraderAllowedEmail(user?.email)) {
        return <Navigate to="/explore" replace />;
    }

    return <GraderPanel />;
}

// Shared pill-input styling — labeled pill config fields per the Frame 2 lock.
const PILL_INPUT_CLASS =
    'w-full rounded-full border border-line bg-transparent px-4 py-2 text-sm text-ink focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent disabled:cursor-not-allowed disabled:bg-ghost-bg disabled:opacity-60';
const FIELD_LABEL_CLASS = 'block pl-0.5 text-xs font-bold uppercase tracking-wide text-ink-muted';
// Three-tier action buttons dim to the app's disabled convention (40%) rather
// than the shared Button's default 50% — `!` forces the win over Button's
// own disabled:opacity-50 utility instead of racing the Tailwind cascade.
const ACTION_BTN_DISABLED_OPACITY = 'disabled:!opacity-40';

function GraderPanel() {
    const [promptId, setPromptId] = useState('');
    const [sampleSize, setSampleSize] = useState(10);
    const [prompt, setPrompt] = useState('');
    const [promptSeeded, setPromptSeeded] = useState(false);

    const [loadingPrompt, setLoadingPrompt] = useState(true);
    const [running, setRunning] = useState(false);
    const [judging, setJudging] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [currentRun, setCurrentRun] = useState<{
        rows: GraderResultRow[];
        aggregates: GraderRunAggregates;
        promptId: string;
        sampleSize: number;
        prompt: string;
        runResponse: GraderRunResponse;
        sampleUrls: string[];
        judged: boolean;
    } | null>(null);

    // Session-local run history for v1 → v2 → v3 comparison. Not persisted.
    const [history, setHistory] = useState<GraderRunRecord[]>([]);

    // Seed the editor from the live brand-safety template (read-only).
    useEffect(() => {
        let cancelled = false;
        graderApi
            .getCurrentPrompt()
            .then((res) => {
                if (cancelled) return;
                setPrompt(res.template);
                setPromptSeeded(true);
            })
            .catch((e) => {
                if (cancelled) return;
                setError(extractError(e, 'Failed to load the current prompt'));
            })
            .finally(() => {
                if (!cancelled) setLoadingPrompt(false);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const runDisabled = running || judging || !prompt.trim() || !promptId.trim();
    const judgeDisabled = !currentRun || judging || running || currentRun.judged;
    const saveDisabled = !currentRun || running || judging;

    const handleRun = useCallback(async () => {
        if (runDisabled) return;
        setError(null);
        setRunning(true);
        setCurrentRun(null);
        try {
            const sample = await graderApi.getSample(promptId.trim(), sampleSize);
            if (sample.samples.length === 0) {
                setError('No scanned URLs found for that blueprint. Try a different blueprint ID.');
                return;
            }
            const sampleUrls = sample.samples.map((s) => s.url);
            const runResponse = await graderApi.run({
                prompt,
                promptId: promptId.trim(),
                sample: sample.samples.map((s) => ({ url: s.url, content: s.content })),
            });
            if (runResponse.error) {
                setError(`Model run failed: ${runResponse.error}`);
            }
            const { rows, aggregates } = buildResultRows(sampleUrls, runResponse.verdicts);
            setCurrentRun({
                rows,
                aggregates,
                promptId: promptId.trim(),
                sampleSize,
                prompt,
                runResponse,
                sampleUrls,
                judged: false,
            });
        } catch (e) {
            setError(extractError(e, 'Run failed'));
        } finally {
            setRunning(false);
        }
    }, [runDisabled, promptId, sampleSize, prompt]);

    const handleJudge = useCallback(async () => {
        if (!currentRun || judging) return;
        setError(null);
        setJudging(true);
        try {
            const judgeRes = await graderApi.judge({
                blueprint: currentRun.prompt,
                verdicts: currentRun.runResponse.verdicts.map((v) => ({ ...v })),
            });
            const { rows, aggregates } = buildResultRows(
                currentRun.sampleUrls,
                currentRun.runResponse.verdicts,
                judgeRes.judged,
            );
            setCurrentRun((prev) => (prev ? { ...prev, rows, aggregates, judged: true } : prev));
        } catch (e) {
            setError(extractError(e, 'Judge failed'));
        } finally {
            setJudging(false);
        }
    }, [currentRun, judging]);

    // Snapshot the current run into the comparison history (assigns vN).
    const handleSaveToComparison = useCallback(() => {
        if (!currentRun) return;
        setHistory((prev) =>
            appendRunRecord(prev, {
                promptId: currentRun.promptId,
                sampleSize: currentRun.sampleSize,
                prompt: currentRun.prompt,
                rows: currentRun.rows,
                aggregates: currentRun.aggregates,
                judged: currentRun.judged,
            }),
        );
    }, [currentRun]);

    const handleClearComparison = useCallback(() => setHistory([]), []);

    return (
        <div data-testid="grader-page-root" className="w-full space-y-3">
            <ActionsRow />
            <ConfigCard
                promptId={promptId}
                onPromptId={setPromptId}
                sampleSize={sampleSize}
                onSampleSize={setSampleSize}
                fieldsDisabled={running || judging}
                prompt={prompt}
                onPromptChange={setPrompt}
                promptLoading={loadingPrompt}
                promptSeeded={promptSeeded}
                onRun={handleRun}
                runDisabled={runDisabled}
                running={running}
                onJudge={handleJudge}
                judgeDisabled={judgeDisabled}
                judging={judging}
                judged={Boolean(currentRun?.judged)}
                onSave={handleSaveToComparison}
                saveDisabled={saveDisabled}
            />
            {error && (
                <div className="rounded-md border border-danger-200 bg-danger-50 px-3 py-2 text-sm text-danger-800">
                    {error}
                </div>
            )}
            {currentRun && (
                <ResultsCard rows={currentRun.rows} aggregates={currentRun.aggregates} judged={currentRun.judged} />
            )}
            {history.length > 0 && <ComparisonCard history={history} onClear={handleClearComparison} />}
        </div>
    );
}

// Muted subtitle + warn-pair "Read / run only" pill, with the private-preview
// email note kept as accent microtext underneath (Frame 2's `.acts` row).
function ActionsRow() {
    return (
        <div>
            {/* -mt-[21px] pulls the row's centerline up to match the
                sidebar's Explore nav pill centerline (deepcontext-jlkv
                mechanism, deepcontext-fbj1.1 — mirrors SearchFilterBar.tsx's
                Blueprints toolbar). */}
            <div className="-mt-[21px] flex flex-wrap items-center gap-2.5">
                <p className="text-sm text-ink-muted">
                    Edit the live brand-safety prompt, run it over a real sample, grade the output — nothing writes
                    to production.
                </p>
                <span
                    data-testid="grader-ro-badge"
                    className="ml-auto inline-flex items-center rounded-full bg-warn-bg px-2.5 py-1 text-xs font-bold text-warn"
                >
                    Read / run only
                </span>
            </div>
            <p className="mt-1 text-xs text-accent-active">
                Private preview: visible only for {GRADER_ALLOWED_EMAIL}.
            </p>
        </div>
    );
}

function clampSampleSize(n: number): number {
    if (!Number.isFinite(n)) return 10;
    return Math.min(50, Math.max(1, Math.round(n)));
}

function PillField({
    htmlFor,
    label,
    hint,
    children,
    className = '',
}: {
    htmlFor: string;
    label: string;
    hint: string;
    children: React.ReactNode;
    className?: string;
}) {
    return (
        <div className={className}>
            <label htmlFor={htmlFor} className={`mb-1 ${FIELD_LABEL_CLASS}`}>
                {label}
            </label>
            {children}
            <p className="mt-1 pl-0.5 text-xs text-ink-muted">{hint}</p>
        </div>
    );
}

// Config card: Blueprint ID + Sample size pill inputs, the seeded monospace
// prompt editor, and the three-tier action row — always rendered (Run =
// gradient, Run quality judge = outline accent, Save to comparison = ghost).
function ConfigCard({
    promptId,
    onPromptId,
    sampleSize,
    onSampleSize,
    fieldsDisabled,
    prompt,
    onPromptChange,
    promptLoading,
    promptSeeded,
    onRun,
    runDisabled,
    running,
    onJudge,
    judgeDisabled,
    judging,
    judged,
    onSave,
    saveDisabled,
}: {
    promptId: string;
    onPromptId: (v: string) => void;
    sampleSize: number;
    onSampleSize: (n: number) => void;
    fieldsDisabled: boolean;
    prompt: string;
    onPromptChange: (v: string) => void;
    promptLoading: boolean;
    promptSeeded: boolean;
    onRun: () => void;
    runDisabled: boolean;
    running: boolean;
    onJudge: () => void;
    judgeDisabled: boolean;
    judging: boolean;
    judged: boolean;
    onSave: () => void;
    saveDisabled: boolean;
}) {
    return (
        <Card>
            <div className="space-y-3 p-4">
                <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                    <PillField
                        className="md:col-span-2"
                        htmlFor="grader-blueprint"
                        label="Blueprint ID"
                        hint="The sample is pulled from real URLs production scanned for this blueprint."
                    >
                        <input
                            id="grader-blueprint"
                            type="text"
                            value={promptId}
                            disabled={fieldsDisabled}
                            onChange={(e) => onPromptId(e.target.value)}
                            placeholder="Paste a blueprint / prompt ID"
                            className={PILL_INPUT_CLASS}
                        />
                    </PillField>
                    <PillField
                        htmlFor="grader-sample-size"
                        label="Sample size N"
                        hint="Between 1 and 50 URLs."
                    >
                        <input
                            id="grader-sample-size"
                            type="number"
                            min={1}
                            max={50}
                            value={sampleSize}
                            disabled={fieldsDisabled}
                            onChange={(e) => onSampleSize(clampSampleSize(Number(e.target.value)))}
                            className={PILL_INPUT_CLASS}
                        />
                    </PillField>
                </div>

                <div>
                    <div className="flex items-center justify-between">
                        <label htmlFor="grader-prompt" className={FIELD_LABEL_CLASS}>
                            Prompt
                        </label>
                        {promptSeeded && <span className="text-xs text-ink-muted">Seeded from the live prompt</span>}
                    </div>
                    <textarea
                        id="grader-prompt"
                        value={promptLoading ? 'Loading current prompt…' : prompt}
                        disabled={promptLoading}
                        onChange={(e) => onPromptChange(e.target.value)}
                        rows={6}
                        className="mt-1 w-full resize-none rounded-xl border border-line bg-transparent px-3.5 py-2.5 font-mono text-xs leading-relaxed text-ink focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent disabled:bg-ghost-bg disabled:opacity-60"
                    />
                </div>

                <div className="flex flex-wrap items-center gap-2 pt-1">
                    <Button
                        type="button"
                        data-testid="grader-run-btn"
                        variant="default"
                        size="sm"
                        onClick={onRun}
                        disabled={runDisabled}
                        className={ACTION_BTN_DISABLED_OPACITY}
                    >
                        {running ? 'Running…' : 'Run'}
                    </Button>
                    <Button
                        type="button"
                        data-testid="grader-judge-btn"
                        variant="outline"
                        size="sm"
                        onClick={onJudge}
                        disabled={judgeDisabled}
                        className={ACTION_BTN_DISABLED_OPACITY}
                    >
                        {judging ? 'Judging…' : judged ? 'Judged' : 'Run quality judge'}
                    </Button>
                    <Button
                        type="button"
                        data-testid="grader-save-btn"
                        variant="ghost"
                        size="sm"
                        onClick={onSave}
                        disabled={saveDisabled}
                        className={ACTION_BTN_DISABLED_OPACITY}
                    >
                        Save to comparison
                    </Button>
                </div>
            </div>
        </Card>
    );
}

function ResultsCard({
    rows,
    aggregates,
    judged,
}: {
    rows: GraderResultRow[];
    aggregates: GraderRunAggregates;
    judged: boolean;
}) {
    return (
        <Card>
            <div className="space-y-3 p-4">
                <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                    <AggCell
                        testId="grader-agg-format"
                        label="Format pass rate"
                        value={`${aggregates.formatScore.toFixed(1)}%`}
                        note={`${aggregates.formatPassedCount}/${aggregates.formatTotalCount} URLs pass deterministic checks`}
                    />
                    <AggCell
                        testId="grader-agg-quality"
                        label="Quality average"
                        value={aggregates.qualityAvg === undefined ? '—' : `${aggregates.qualityAvg.toFixed(2)}/10`}
                        note={
                            judged
                                ? `Judged by model over ${aggregates.qualityJudgedCount} URLs`
                                : 'Run the quality judge to score verdicts 0–10.'
                        }
                    />
                </div>
                <ResultsTable rows={rows} />
            </div>
        </Card>
    );
}

// Canvas-toned aggregate cell nested inside the surface card (the two-tone
// contrast the lock uses to set the run-level numbers apart from the table).
function AggCell({ testId, label, value, note }: { testId: string; label: string; value: string; note: string }) {
    return (
        <div data-testid={testId} className="rounded-xl bg-canvas px-3.5 py-3">
            <p className="text-xs font-bold uppercase tracking-wide text-ink-muted">{label}</p>
            <p className="mt-0.5 flex flex-wrap items-baseline gap-1.5">
                <span className="text-sm font-extrabold tabular-nums text-ink">{value}</span>
                <span className="text-xs text-ink-muted">{note}</span>
            </p>
        </div>
    );
}

function ResultsTable({ rows }: { rows: GraderResultRow[] }) {
    return (
        <div className="overflow-x-auto rounded-xl border border-line">
            <Table>
                <TableHead>
                    <TableRow className="hover:bg-transparent">
                        <TableHeaderCell>URL</TableHeaderCell>
                        <TableHeaderCell>Verdict</TableHeaderCell>
                        <TableHeaderCell className="text-center">Format</TableHeaderCell>
                        <TableHeaderCell className="text-right">Quality</TableHeaderCell>
                        <TableHeaderCell>Judge reasoning</TableHeaderCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((row) => (
                        <ResultRow key={row.url} row={row} />
                    ))}
                </TableBody>
            </Table>
        </div>
    );
}

function ResultRow({ row }: { row: GraderResultRow }) {
    const verdict = row.verdict;
    return (
        <TableRow data-testid="grader-result-row">
            <TableCell className="max-w-[18rem] align-top text-xs font-semibold text-accent-active">
                <span className="break-all">{row.url}</span>
            </TableCell>
            <TableCell className="align-top">
                {verdict ? (
                    <VerdictPill isBrandSafe={verdict.isBrandSafe} confidence={verdict.confidence} />
                ) : (
                    <span className="text-xs text-ink-muted">—</span>
                )}
            </TableCell>
            <TableCell className="text-center align-top">
                <FormatBadge pass={row.formatPass} failures={row.formatFailures} />
            </TableCell>
            <TableCell className="text-right align-top tabular-nums">
                {row.qualityScore === undefined ? (
                    <span className="text-xs text-ink-muted">—</span>
                ) : (
                    `${row.qualityScore.toFixed(1)}`
                )}
            </TableCell>
            <TableCell className="max-w-[22rem] align-top text-xs text-ink-muted">
                {row.judgeReasoning ? row.judgeReasoning : verdict?.reasoning || ''}
            </TableCell>
        </TableRow>
    );
}

// Verdict pill carrying confidence — Safe wears the safe token pair, Unsafe
// the risk pair (per the lock's "verdict pills carry confidence" rule).
function VerdictPill({ isBrandSafe, confidence }: { isBrandSafe: boolean; confidence: number }) {
    return (
        <Badge variant={isBrandSafe ? 'safe' : 'risk'}>
            {isBrandSafe ? 'Safe' : 'Unsafe'}
            {Number.isFinite(confidence) ? ` · ${formatConfidence(confidence)}` : ''}
        </Badge>
    );
}

function formatConfidence(confidence: number): string {
    const normalized = confidence <= 1 ? confidence * 100 : confidence;
    return `${Math.round(normalized)}%`;
}

function FormatBadge({ pass, failures }: { pass: boolean; failures: string[] }) {
    return (
        <span
            className="group relative inline-flex items-center text-base leading-none"
            role="img"
            aria-label={pass ? 'Format pass' : `Format fail: ${failures.join(', ')}`}
            title={pass ? 'Pass' : failures.join(', ')}
        >
            {pass ? '✅' : '❌'}
        </span>
    );
}

// Session-local comparison of successive runs (v1 → v2 → v3). One row per saved
// run; shows the two aggregates side by side so prompt edits are comparable
// within the session. Not persisted across reloads (out of scope for v1).
function ComparisonCard({ history, onClear }: { history: GraderRunRecord[]; onClear: () => void }) {
    const best = useMemo(() => bestVersions(history), [history]);
    return (
        <Card>
            <div className="space-y-2.5 p-4">
                <div className="flex items-center justify-between">
                    <h2 className="text-sm font-bold text-ink">Run comparison (this session)</h2>
                    <button
                        type="button"
                        onClick={onClear}
                        className="text-xs font-bold text-ink-muted hover:text-ink"
                    >
                        Clear
                    </button>
                </div>
                <div className="overflow-x-auto rounded-xl border border-line">
                    <Table>
                        <TableHead>
                            <TableRow className="hover:bg-transparent">
                                <TableHeaderCell>Run</TableHeaderCell>
                                <TableHeaderCell>Blueprint</TableHeaderCell>
                                <TableHeaderCell className="text-right">N</TableHeaderCell>
                                <TableHeaderCell className="text-right">Format %</TableHeaderCell>
                                <TableHeaderCell className="text-right">Quality avg</TableHeaderCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {history.map((run) => (
                                <TableRow key={run.version} data-testid="grader-comparison-row">
                                    <TableCell className="font-bold">{run.label}</TableCell>
                                    <TableCell className="max-w-[16rem] text-xs text-ink-muted">
                                        <span className="break-all">{run.promptId}</span>
                                    </TableCell>
                                    <TableCell className="text-right tabular-nums">{run.sampleSize}</TableCell>
                                    <TableCell
                                        data-testid={`grader-compare-format-${run.version}`}
                                        className={`text-right tabular-nums ${
                                            best.format === run.version ? 'font-bold text-safe' : ''
                                        }`}
                                    >
                                        {run.aggregates.formatScore.toFixed(1)}%
                                    </TableCell>
                                    <TableCell
                                        data-testid={`grader-compare-quality-${run.version}`}
                                        className={`text-right tabular-nums ${
                                            best.quality === run.version ? 'font-bold text-safe' : ''
                                        }`}
                                    >
                                        {run.aggregates.qualityAvg === undefined
                                            ? '—'
                                            : run.aggregates.qualityAvg.toFixed(2)}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            </div>
        </Card>
    );
}

function bestVersions(history: GraderRunRecord[]): { format: number | null; quality: number | null } {
    let format: { version: number; score: number } | null = null;
    let quality: { version: number; score: number } | null = null;
    for (const run of history) {
        if (format === null || run.aggregates.formatScore > format.score) {
            format = { version: run.version, score: run.aggregates.formatScore };
        }
        const q = run.aggregates.qualityAvg;
        if (q !== undefined && (quality === null || q > quality.score)) {
            quality = { version: run.version, score: q };
        }
    }
    return { format: format?.version ?? null, quality: quality?.version ?? null };
}

function extractError(e: unknown, fallback: string): string {
    const err = e as { response?: { data?: { error?: { message?: string } } }; message?: string };
    return err?.response?.data?.error?.message || err?.message || fallback;
}
